/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import WhatsLayout from "../layouts/WhatsLayout";
import { FaCheck } from "react-icons/fa";
import { useUI } from "../theme/ThemeProvider";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";
import AdminBanners from "./Banner";
const THEMES = [
  {
    id: 1,
    name: "Classic",
    variants: [
      {
        name: "Classic Brown",
        colors: {
          primary: "#C18064",
          primaryDark: "#A66B52",
          background: "#FAF5F3",
          secondaryBackground: "#FFF9E6",
        },
        img: "/themes/classic/brown.png",
      },
      {
        name: "Ocean Blue",
        colors: {
          primary: "#4A90E2",
          primaryDark: "#357ABD",
          background: "#F0F7FF",
          secondaryBackground: "#E6F2FF",
        },
        img: "/themes/classic/blue.png",
      },
      {
        name: "Royal Purple",
        colors: {
          primary: "#9B59B6",
          primaryDark: "#8E44AD",
          background: "#F8F4FB",
          secondaryBackground: "#F3E8FF",
        },
        img: "/themes/classic/purple.png",
      },
      {
        name: "Coral Red",
        colors: {
          primary: "#E74C3C",
          primaryDark: "#C0392B",
          background: "#FFF5F3",
          secondaryBackground: "#FFE6E3",
        },
        img: "/themes/classic/red.png",
      },
    ],
  },

  {
    id: 2,
    name: "Glow",
    variants: [
      {
        name: "Pink Glow",
        colors: {
          primary: "#EC4899",
          primaryDark: "#DB2777",
          primaryLight: "#F472B6",
          accent: "#FCD34D",
          accentDark: "#F59E0B",
        },
        img: "/themes/glow/pink.png",
      },
      {
        name: "Purple Dream",
        colors: {
          primary: "#A855F7",
          primaryDark: "#9333EA",
          primaryLight: "#C084FC",
          accent: "#F0ABFC",
          accentDark: "#D946EF",
        },
        img: "/themes/glow/purple.png",
      },
      {
        name: "Golden Luxe",
        colors: {
          primary: "#F59E0B",
          primaryDark: "#D97706",
          primaryLight: "#FBBF24",
          accent: "#FCD34D",
          accentDark: "#F59E0B",
        },
        img: "/themes/glow/golden.png",
      },
      {
        name: "Turquoise Glow",
        colors: {
          primary: "#14B8A6",
          primaryDark: "#0D9488",
          primaryLight: "#2DD4BF",
          accent: "#5EEAD4",
          accentDark: "#14B8A6",
        },
        img: "/themes/glow/turquoise.png",
      },
    ],
  },

  {
    id: 3,
    name: "Modern",
    variants: [
      { name: "original", colors: { primary: "#0E3981", primaryDark: "#112758", background: "#F3F8FB", text: "#0E3981", bg: "#0E3981", hover: "#112758" }, img: "/themes/modern/original.png" },
      { name: "royalBlue", colors: { primary: "#2563eb", primaryDark: "#1d4ed8", background: "#F9FAFB", text: "#2563eb", bg: "#2563eb", hover: "#1d4ed8" }, img: "/themes/modern/royal-blue.png" },
      { name: "forestGreen", colors: { primary: "#059669", primaryDark: "#047857", background: "#F9FAFB", text: "#059669", bg: "#059669", hover: "#047857" }, img: "/themes/modern/forest-green.png" },
      { name: "burntOrange", colors: { primary: "#ea580c", primaryDark: "#c2410c", background: "#F9FAFB", text: "#ea580c", bg: "#ea580c", hover: "#c2410c" }, img: "/themes/modern/burnt-orange.png" },
    ],
  },

];
export default function ThemesPage() {
  const { theme, lang, colors: themeColors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const T = (en, ar) => (isArabic ? ar : en);
  const perms = useTenantStorePermissions();
  const { colors: uiColors } = useUI();
  const colors = uiColors || themeColors;

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  const [themeVariantMap, setThemeVariantMap] = useState({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentTenantData, setCurrentTenantData] = useState(null);
  const [selectedThemeId, setSelectedThemeId] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);




  const handleThemeClick = (theme) => {
    const lastVariant = themeVariantMap[theme.id] || theme.variants[0];
    setSelectedThemeId(theme.id);
    setSelectedVariant(lastVariant);
  };

  const handleVariantClick = (themeId, variant) => {
    setSelectedThemeId(themeId);
    setSelectedVariant(variant);

    setThemeVariantMap(prev => ({
      ...prev,
      [themeId]: variant,
    }));
  };





  // ✅ جلب بيانات التينانت + استرجاع اللون
  useEffect(() => {
    const fetchTenantData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        // 🔥 إرسال tenantId داخل الاستعلام
        const res = await fetch(
          `${baseUrl}/GetAllTenantFullDetails?tenantId=${tenantId}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: token ? `Bearer ${token}` : "",
            },
          }
        );

        if (!res.ok) throw new Error("Failed to fetch tenant data");

        const data = await res.json();

        // الـ API قد ترجع Array أو Object
        const tenant = Array.isArray(data) ? data[0] : data;

        if (!tenant) return;

        setCurrentTenantData(tenant);

        const themeId = tenant.ThemeId || 1;
        const colorName = tenant.ColorName;

        const themeObj = THEMES.find(t => t.id === themeId);
        if (themeObj) {
          const variant =
            themeObj.variants.find(v => v.name === colorName) ||
            themeObj.variants[0];

          setSelectedThemeId(themeObj.id);
          setSelectedVariant(variant);

          setThemeVariantMap(prev => ({
            ...prev,
            [themeObj.id]: variant,
          }));

        }




      } catch (e) {
        console.error("❌ Error loading tenant:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, [tenantId]);



  const handleSave = async () => {
    if (!perms.editTheme) return;

    setSaving(true);

    try {
      const themeId = selectedThemeId;

      const token = localStorage.getItem("authToken");

      const formData = new FormData();

      // -----------------------------
      // 🟢 القيم الإلزامية التي يجب إرسالها كلها
      // -----------------------------
      formData.append("TenantId", tenantId);
      formData.append("TenantName", currentTenantData?.TenantName || "");
      formData.append("EngTenantName", currentTenantData?.EngTenantName || "");
      formData.append("Email", currentTenantData?.Email || "");
      formData.append("Phone", currentTenantData?.Phone || "");
      formData.append("SubDomain", currentTenantData?.SubDomain || "");
      formData.append("IsActive", currentTenantData?.IsActive ?? 1);
      formData.append("CreatedAt", currentTenantData?.CreatedAt || new Date().toISOString());
      formData.append("Product", currentTenantData?.Product || "");
      formData.append("MainPlanId", currentTenantData?.MainPlanId || 0);
      formData.append("TenantDtlId", currentTenantData?.TenantDtlId || 0);

      // LogoFile (نتركه فاضي)
      formData.append("LogoFile", "");

      formData.append("ClientName", currentTenantData?.ClientName || "");
      formData.append("SectorType", currentTenantData?.SectorType || "");
      formData.append("BusinessName", currentTenantData?.BusinessName || "");
      formData.append("CompanyName", currentTenantData?.CompanyName || "");

      // -----------------------------
      // 🟣 الثيم واللون (المطلوبين)
      // -----------------------------
      formData.append("ThemeId", selectedThemeId);
      formData.append("ColorName", selectedVariant?.name || "");


      // -----------------------------
      // باقي الحقول المطلوبة
      // -----------------------------
      formData.append("DetailPlanId", currentTenantData?.DetailPlanId || 0);
      formData.append("PlanType", currentTenantData?.PlanType || "");
      formData.append("EngPlanType", currentTenantData?.EngPlanType || "");
      formData.append("Price", currentTenantData?.Price || 0);
      formData.append("Descripsion", currentTenantData?.Descripsion || "");
      formData.append("EngDescripsion", currentTenantData?.EngDescripsion || "");
      formData.append("Note1", currentTenantData?.Note1 || "");
      formData.append("Note2", currentTenantData?.Note2 || "");
      formData.append("Note3", currentTenantData?.Note3 || "");
      formData.append("Note4", currentTenantData?.Note4 || "");
      formData.append("Note5", currentTenantData?.Note5 || "");
      formData.append("Note6", currentTenantData?.Note6 || "");
      formData.append("Note7", currentTenantData?.Note7 || "");
      formData.append("Note8", currentTenantData?.Note8 || "");

      // -----------------------------
      // السوشيال ميديا (إن وجدت)
      // -----------------------------
      formData.append("Facebook", currentTenantData?.Facebook || "");
      formData.append("Instagram", currentTenantData?.Instagram || "");
      formData.append("Snapchat", currentTenantData?.Snapchat || "");
      formData.append("Tiktok", currentTenantData?.Tiktok || "");
      formData.append("Whatsapp", currentTenantData?.Whatsapp || "");
      formData.append("PrivacyPolicy", currentTenantData?.PrivacyPolicy || "");
      formData.append("DeliveryPolicy", currentTenantData?.DeliveryPolicy || "");
      formData.append("RefundPolicy", currentTenantData?.RefundPolicy || "");
      formData.append("StoreSlug", currentTenantData?.StoreSlug || "");

      // -----------------------------
      // Payment Methods (إن وجدت)
      // -----------------------------
      if (Array.isArray(currentTenantData?.PaymentMethods)) {
        formData.append("PaymentMethods", JSON.stringify(currentTenantData.PaymentMethods));
      }

      // -----------------------------
      // إرسال الطلب
      // -----------------------------
      const res = await fetch(`${baseUrl}/UpdateTenantDetails/UpdateTenantFullDetails`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      if (!res.ok) throw new Error("Save failed");

      Swal.fire({
        icon: "success",
        title: T("Success", "نجح"),
        text: T("Theme & color saved successfully", "تم حفظ الثيم واللون بنجاح"),
        confirmButtonColor: colors.green,
      });

    } catch (e) {
      Swal.fire({
        icon: "error",
        title: T("Error", "خطأ"),
        text: e.message,
        confirmButtonColor: colors.green,
      });
    } finally {
      setSaving(false);
    }
  };


  if (!perms.viewTheme && !perms.viewBanners) return <NoAccess />;

  const pageTitle = perms.viewTheme && perms.viewBanners
    ? T("Themes & Banners", "الثيمات واللافتات")
    : perms.viewTheme
      ? T("Themes", "الثيمات")
      : T("Banners", "اللافتات");

  return (
    <WhatsLayout title={pageTitle}>
      <section
        dir={isArabic ? "rtl" : "ltr"}
        className={`min-h-screen px-5 sm:px-10 py-10 transition-all duration-300 ${isDark ? "text-gray-100" : "text-gray-900"
          }`}
        style={{
          background: isDark
            ? "linear-gradient(180deg, #0B1020 0%, #131827 100%)"
            : "linear-gradient(180deg, #F8FAFC 0%, #E2E8F0 100%)",
        }}
      >
        {perms.viewTheme && (
        <>
        {/* ===== العنوان الرئيسي ===== */}
        <div className="text-center mb-8">
          <h1
            className="text-3xl font-bold mb-2"
            style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}
          >
            {T("Choose Your Theme", "اختر ثيمتك")}
          </h1>
          <p
            className="text-sm"
            style={{ color: isDark ? "#94a3b8" : "#64748b" }}
          >
            {T(
              "Select a theme to customize your store appearance",
              "اختر ثيماً لتخصيص مظهر متجرك"
            )}
          </p>
        </div>

        {/* ===== زر الحفظ ===== */}
        <div className="flex justify-center mb-10">
          {perms.editTheme && (
            <motion.button
              whileHover={{ scale: saving ? 1 : 1.05 }}
              whileTap={{ scale: saving ? 1 : 0.95 }}
              onClick={handleSave}
              disabled={loading || saving}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold shadow-lg transition-all duration-300"
              style={{
                background: loading || saving ? "#94a3b8" : colors.green,
                color: "#ffffff",
                cursor: loading || saving ? "not-allowed" : "pointer",
                opacity: loading || saving ? 0.7 : 1,
              }}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {T("Saving...", "جاري الحفظ...")}
                </>
              ) : (
                <>
                  <FaCheck /> {T("Save Changes", "حفظ التغييرات")}
                </>
              )}
            </motion.button>
          )}
        </div>

        {/* ===== شبكة الثيمات ===== */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {THEMES.map((themeItem, index) => {
            const isSelected = themeItem.id === selectedThemeId;
            const activeVariant = isSelected
              ? themeVariantMap[themeItem.id] || themeItem.variants[0]
              : themeItem.variants[0];


            return (
              <motion.div
                key={themeItem.id}
                onClick={() => handleThemeClick(themeItem)}
                className="relative rounded-2xl p-6 border-2 cursor-pointer"
                style={{
                  borderColor: isSelected ? "#22C55E" : "#334155",
                }}
              >
                {/* صورة اللون المختار */}
                {/* --- بداية تعديل تأثير التمرير الذكي --- */}
                <div className="relative w-full h-64 overflow-hidden rounded-xl mb-4 group border border-gray-100 shadow-inner bg-white">
                  <img
                    src={activeVariant.img}
                    alt={themeItem.name}
                    className="w-full h-auto transition-all duration-[4000ms] ease-in-out cursor-pointer align-top"
                    style={{
                      transform: "translateY(0%)",
                    }}
                    onMouseEnter={(e) => {
                      const imgHeight = e.currentTarget.offsetHeight;
                      const containerHeight = 256; // نفس ارتفاع h-64
                      if (imgHeight > containerHeight) {
                        e.currentTarget.style.transform = `translateY(-${imgHeight - containerHeight}px)`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0%)";
                    }}
                  />
                  {/* تلميح للمستخدم يظهر عند الحوم بالماوس */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <span className="bg-black/60 text-white text-[10px] px-2 py-1 rounded-full backdrop-blur-sm">
                      {T("Scrolling preview...", "عرض التصميم كامل...")}
                    </span>
                  </div>
                </div>
                {/* --- نهاية التعديل --- */}

                <h3 className="text-center font-bold mb-3">
                  {themeItem.name}
                </h3>

                {/* الألوان */}
                <div className="flex justify-center gap-2">
                  {themeItem.variants.map(v => (
                    <div
                      key={v.name}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVariantClick(themeItem.id, v);
                      }}
                      className={`w-8 h-8 rounded-full border-2 cursor-pointer ${isSelected && selectedVariant?.name === v.name
                          ? "ring-4 ring-green-500"
                          : ""
                        }`}
                      style={{ background: v.colors.primary }}
                    />
                  ))}

                </div>

                {isSelected && (
                  <div className="absolute top-3 right-3 bg-green-500 text-white p-2 rounded-full">
                    <FaCheck size={12} />
                  </div>
                )}
              </motion.div>
            );
          })}

        </div>

        {/* ===== معلومات إضافية ===== */}
        <div
          className="mt-10 max-w-3xl mx-auto p-6 rounded-2xl"
          style={{
            backgroundColor: isDark
              ? "rgba(30, 41, 59, 0.5)"
              : "rgba(255, 255, 255, 0.7)",
            border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
          }}
        >
          <p
            className="text-center text-sm"
            style={{ color: isDark ? "#94a3b8" : "#64748b" }}
          >
            {T(
              "💡 Tip: Hover over a theme to preview it. Click to select and save your changes.",
              "💡 نصيحة: مرر الماوس فوق الثيم لمعاينته. انقر لتحديده واحفظ التغييرات."
            )}
          </p>
        </div>
        </>
        )}

        {perms.viewBanners && (
          <AdminBanners embedded showDivider={!!perms.viewTheme} />
        )}
      </section>

    </WhatsLayout>
  );
}
